import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  AlertCircle, ArrowLeft, Droplets, Pill, Phone, Stethoscope,
  AlertTriangle, Heart, User, X, Shield, QrCode, Copy, Share2, Lock, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';
import QRCode from 'qrcode';
import useSensitiveAction from '@/hooks/useSensitiveAction';
import VerifyIdentityDialog from '@/components/VerifyIdentityDialog';
import { useDemo } from '@/contexts/DemoContext';

/**
 * EmergencyModePage - MEDICAL ALERT SCREEN
 * 
 * Now reads from shared medical data (single source of truth)
 * Protected by biometric verification when sensitive action protection is enabled
 * 
 * Design for EMT Readability:
 * - Maximum contrast (white on dark colors)
 * - Extra large text (4xl-7xl font sizes)
 * - Simple block layout for quick scanning
 * - Critical info prominently displayed
 * - One-tap phone calls
 */

const EmergencyModePage = () => {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const { isDemoMode, getProfiles, getMedicalProfile, getAllergies, getMedications, getConditions, getDoctors, getInsurance, getEmergencyContacts } = useDemo();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [showQR, setShowQR] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [accessToken, setAccessToken] = useState(null);
  const [generatingQR, setGeneratingQR] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [showVerifyDialog, setShowVerifyDialog] = useState(false);
  
  // Sensitive action protection
  const { willRequireVerification, executeSensitiveAction } = useSensitiveAction();

  // Check if verification is needed on page load
  useEffect(() => {
    if (willRequireVerification()) {
      setShowVerifyDialog(true);
    } else {
      setIsVerified(true);
    }
  }, []);

  // Only fetch data after verification
  useEffect(() => {
    if (isVerified) {
      fetchData();
    }
  }, [isVerified, profileId, isDemoMode]);

  const handleVerificationSuccess = () => {
    setIsVerified(true);
    setShowVerifyDialog(false);
  };

  const handleVerificationCancel = () => {
    setShowVerifyDialog(false);
    navigate(-1); // Go back if user cancels verification
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Demo mode: build summary from local demo data
      if (isDemoMode) {
        const profiles = getProfiles() || [];
        const demoProfile = profiles.find(p => p.id === profileId) || profiles[0];
        const medicalProfile = getMedicalProfile() || {};
        const allergies = getAllergies() || [];
        const medications = getMedications() || [];
        const conditions = getConditions() || [];
        const doctors = getDoctors() || [];
        const insurance = getInsurance() || [];
        const emergencyContacts = getEmergencyContacts() || [];
        const primaryDoctor = doctors[0];
        const primaryInsurance = insurance[0];
        const primaryEmergencyContact = emergencyContacts[0];
        
        const demoSummary = {
          profile_name: demoProfile?.name || medicalProfile.full_name || 'Demo User',
          date_of_birth: medicalProfile.date_of_birth || '',
          blood_type: medicalProfile.blood_type || '',
          allergies: allergies.map(a => a.name),
          medications: medications.map(m => `${m.name}${m.dosage ? ` ${m.dosage}` : ''}`),
          conditions: conditions.map(c => c.condition || c.name),
          primary_doctor: primaryDoctor?.name || '',
          primary_doctor_phone: primaryDoctor?.phone || '',
          insurance_provider: primaryInsurance?.provider || '',
          insurance_policy_number: primaryInsurance?.policy_number || '',
          emergency_contact: primaryEmergencyContact?.name || '',
          emergency_contact_relationship: primaryEmergencyContact?.relationship || '',
          emergency_contact_phone: primaryEmergencyContact?.phone || ''
        };
        
        setSummary(demoSummary);
        
        // Generate a demo QR code
        try {
          const demoShareUrl = `${window.location.origin}/emergency-view/demo-access-token`;
          const qrUrl = await QRCode.toDataURL(demoShareUrl, {
            width: 300,
            margin: 2,
            color: { dark: '#000000', light: '#ffffff' }
          });
          setQrCodeUrl(qrUrl);
          setAccessToken('demo-access-token');
        } catch (e) {
          console.log('QR generation skipped');
        }
        
        setLoading(false);
        return;
      }
      
      // Use shared medical emergency summary - single source of truth
      const summaryRes = await API.get('/shared-medical/emergency-summary');
      setSummary(summaryRes.data);
      
      // Get QR token if exists
      if (profileId) {
        try {
          const tokenRes = await API.get(`/emergency-binder/profiles/${profileId}/access-token`);
          if (tokenRes.data.access_token) {
            setAccessToken(tokenRes.data.access_token);
            const shareUrl = `${window.location.origin}/emergency-view/${tokenRes.data.access_token}`;
            const qrUrl = await QRCode.toDataURL(shareUrl, {
              width: 300,
              margin: 2,
              color: { dark: '#000000', light: '#ffffff' }
            });
            setQrCodeUrl(qrUrl);
          }
        } catch (e) {
          // Token not available yet
        }
      }
    } catch (error) {
      console.error('Failed to fetch emergency data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Generate QR code on demand
  const handleShowQR = async () => {
    if (accessToken && qrCodeUrl) {
      setShowQR(true);
      return;
    }
    
    // Demo mode: use demo token
    if (isDemoMode) {
      try {
        setGeneratingQR(true);
        const demoShareUrl = `${window.location.origin}/emergency-view/demo-access-token`;
        const qrUrl = await QRCode.toDataURL(demoShareUrl, {
          width: 300,
          margin: 2,
          color: { dark: '#000000', light: '#ffffff' }
        });
        setQrCodeUrl(qrUrl);
        setAccessToken('demo-access-token');
        setShowQR(true);
      } catch (e) {
        toast.error('Failed to generate QR code');
      } finally {
        setGeneratingQR(false);
      }
      return;
    }
    
    try {
      setGeneratingQR(true);
      const response = await API.post(`/emergency-binder/profiles/${profileId}/generate-access-token`);
      const token = response.data.access_token;
      setAccessToken(token);
      
      const shareUrl = `${window.location.origin}/emergency-view/${token}`;
      const qrUrl = await QRCode.toDataURL(shareUrl, {
        width: 300,
        margin: 2,
        color: { dark: '#000000', light: '#ffffff' }
      });
      setQrCodeUrl(qrUrl);
      setShowQR(true);
    } catch (error) {
      console.error('Failed to generate QR:', error);
      if (!isDemoMode) {
        toast.error('Failed to generate QR code');
      }
    } finally {
      setGeneratingQR(false);
    }
  };

  const handleCopyLink = async () => {
    if (!accessToken) return;
    const shareUrl = `${window.location.origin}/emergency-view/${accessToken}`;
    await navigator.clipboard.writeText(shareUrl);
    toast.success('Emergency link copied!');
  };

  const handleShare = async () => {
    if (!accessToken) return;
    const shareUrl = `${window.location.origin}/emergency-view/${accessToken}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${summary?.profile_name}'s Emergency Info`,
          text: 'Scan or tap to view emergency medical information',
          url: shareUrl
        });
      } catch (e) {
        // User cancelled or error
        handleCopyLink();
      }
    } else {
      handleCopyLink();
    }
  };

  // Show verification dialog if needed
  if (showVerifyDialog) {
    return (
      <VerifyIdentityDialog
        open={true}
        onClose={handleVerificationCancel}
        onSuccess={handleVerificationSuccess}
        title="Verify to View"
        description="This screen contains sensitive medical information"
        actionLabel="View Emergency Info"
      />
    );
  }

  // Show loading while verifying or fetching
  if (!isVerified || loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-white animate-spin mx-auto mb-4" />
          <p className="text-white/60">{!isVerified ? 'Verifying...' : 'Loading...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen bg-black text-white" 
      data-testid="emergency-mode-page"
      style={{ WebkitTextSizeAdjust: 'none' }}
    >
      {/* Header Bar */}
      <header className="bg-red-600 p-3 flex items-center justify-between sticky top-0 z-50">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/emergency-binder')}
          className="text-white hover:bg-red-700 h-12 w-12"
        >
          <ArrowLeft className="h-8 w-8" />
        </Button>
        <div className="flex items-center gap-2">
          <AlertCircle className="h-8 w-8 animate-pulse" />
          <span className="font-black text-2xl tracking-wider">MEDICAL ALERT</span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/emergency-binder')}
          className="text-white hover:bg-red-700 h-12 w-12"
        >
          <X className="h-8 w-8" />
        </Button>
      </header>

      <main className="p-4 space-y-4 pb-24">
        {/* PATIENT NAME - Largest Element */}
        <section className="bg-slate-900 rounded-2xl p-6 text-center border-2 border-slate-700">
          <div className="flex items-center justify-center gap-4 mb-2">
            <User className="h-10 w-10 text-slate-400" />
            <span className="text-slate-400 text-xl font-semibold uppercase tracking-wide">Patient</span>
          </div>
          <h1 className="text-5xl sm:text-6xl font-black text-white leading-tight">
            {summary?.profile_name}
          </h1>
          {summary?.date_of_birth && (
            <p className="text-2xl text-slate-300 mt-3 font-semibold">
              DOB: {summary.date_of_birth}
            </p>
          )}
        </section>

        {/* BLOOD TYPE - Highest Visibility */}
        {summary?.blood_type && (
          <section className="bg-white rounded-2xl p-8 text-center">
            <div className="flex items-center justify-center gap-3 mb-3">
              <Droplets className="h-10 w-10 text-red-600" />
              <span className="text-xl font-bold text-red-600 uppercase tracking-wide">Blood Type</span>
            </div>
            <p className="text-8xl sm:text-9xl font-black text-red-600 leading-none">
              {summary.blood_type}
            </p>
          </section>
        )}

        {/* ALLERGIES - Critical Warning */}
        <section className="bg-amber-500 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4 border-b border-amber-400 pb-3">
            <AlertTriangle className="h-10 w-10" />
            <span className="text-3xl font-black uppercase tracking-wide">Allergies</span>
          </div>
          {summary?.allergies && summary.allergies.length > 0 ? (
            <ul className="space-y-4">
              {summary.allergies.map((allergy, index) => (
                <li key={index} className="flex items-center gap-4">
                  <span className="w-4 h-4 bg-white rounded-full flex-shrink-0" />
                  <span className="text-3xl sm:text-4xl font-bold">{allergy}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-3xl font-bold text-amber-100">NO KNOWN ALLERGIES</p>
          )}
        </section>

        {/* MEDICATIONS */}
        <section className="bg-blue-600 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4 border-b border-blue-500 pb-3">
            <Pill className="h-10 w-10" />
            <span className="text-3xl font-black uppercase tracking-wide">Medications</span>
          </div>
          {summary?.medications && summary.medications.length > 0 ? (
            <ul className="space-y-4">
              {summary.medications.map((med, index) => (
                <li key={index} className="flex items-center gap-4">
                  <span className="w-4 h-4 bg-white rounded-full flex-shrink-0" />
                  <span className="text-2xl sm:text-3xl font-bold">{med}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-2xl font-bold text-blue-200">NO CURRENT MEDICATIONS</p>
          )}
        </section>

        {/* MEDICAL CONDITIONS */}
        {summary?.conditions && summary.conditions.length > 0 && (
          <section className="bg-purple-700 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4 border-b border-purple-600 pb-3">
              <Heart className="h-10 w-10" />
              <span className="text-3xl font-black uppercase tracking-wide">Conditions</span>
            </div>
            <ul className="space-y-4">
              {summary.conditions.map((condition, index) => (
                <li key={index} className="flex items-center gap-4">
                  <span className="w-4 h-4 bg-white rounded-full flex-shrink-0" />
                  <span className="text-2xl sm:text-3xl font-bold">{condition}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* EMERGENCY CONTACT - Tap to Call */}
        {summary?.emergency_contact && (
          <section className="bg-emerald-600 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4 border-b border-emerald-500 pb-3">
              <Phone className="h-10 w-10" />
              <span className="text-3xl font-black uppercase tracking-wide">Emergency Contact</span>
            </div>
            <p className="text-3xl font-bold mb-1">{summary.emergency_contact}</p>
            {summary.emergency_contact_relationship && (
              <p className="text-xl text-emerald-200 mb-4">
                ({summary.emergency_contact_relationship})
              </p>
            )}
            {summary.emergency_contact_phone && (
              <a 
                href={`tel:${summary.emergency_contact_phone}`}
                className="block w-full bg-white text-emerald-700 py-5 px-6 rounded-xl text-center"
              >
                <span className="text-lg font-semibold block mb-1">TAP TO CALL</span>
                <span className="text-4xl font-black">{summary.emergency_contact_phone}</span>
              </a>
            )}
          </section>
        )}

        {/* PRIMARY DOCTOR */}
        {summary?.primary_doctor && (
          <section className="bg-slate-700 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4 border-b border-slate-600 pb-3">
              <Stethoscope className="h-10 w-10" />
              <span className="text-3xl font-black uppercase tracking-wide">Primary Doctor</span>
            </div>
            <p className="text-3xl font-bold mb-2">{summary.primary_doctor}</p>
            {summary.primary_doctor_phone && (
              <a 
                href={`tel:${summary.primary_doctor_phone}`}
                className="block w-full bg-white text-slate-800 py-4 px-6 rounded-xl text-center"
              >
                <span className="text-lg font-semibold block mb-1">TAP TO CALL</span>
                <span className="text-3xl font-black">{summary.primary_doctor_phone}</span>
              </a>
            )}
          </section>
        )}

        {/* INSURANCE */}
        {summary?.insurance_provider && (
          <section className="bg-pink-600 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4 border-b border-pink-500 pb-3">
              <Shield className="h-10 w-10" />
              <span className="text-3xl font-black uppercase tracking-wide">Insurance</span>
            </div>
            <p className="text-3xl font-bold mb-2">{summary.insurance_provider}</p>
            {summary.insurance_policy_number && (
              <p className="text-xl text-pink-200">
                Policy #: <span className="font-bold">{summary.insurance_policy_number}</span>
              </p>
            )}
          </section>
        )}
      </main>

      {/* Fixed Bottom Bar with QR and Exit */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black via-black to-transparent">
        <div className="flex gap-3 max-w-lg mx-auto">
          <Button
            onClick={handleShowQR}
            disabled={generatingQR}
            className="flex-1 h-16 bg-white hover:bg-slate-100 text-black text-lg font-bold rounded-xl"
          >
            {generatingQR ? (
              <div className="h-6 w-6 border-2 border-black border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <QrCode className="h-7 w-7 mr-3" />
                SHOW QR
              </>
            )}
          </Button>
          <Button
            onClick={() => navigate('/emergency-binder')}
            className="flex-1 h-16 bg-red-600 hover:bg-red-700 text-white text-lg font-black rounded-xl"
          >
            EXIT
          </Button>
        </div>
      </div>

      {/* QR Code Modal - Full Screen for Easy Scanning */}
      {showQR && qrCodeUrl && (
        <div 
          className="fixed inset-0 z-[100] bg-white flex flex-col items-center justify-center p-6"
          onClick={() => setShowQR(false)}
          data-testid="emergency-qr-modal"
        >
          <div className="text-center" onClick={(e) => e.stopPropagation()}>
            <div className="bg-red-600 text-white px-6 py-3 rounded-xl mb-6 inline-flex items-center gap-2">
              <AlertCircle className="h-6 w-6" />
              <span className="text-xl font-bold">EMERGENCY QR CODE</span>
            </div>
            
            <h2 className="text-3xl font-black text-slate-800 mb-2">
              {summary?.profile_name}
            </h2>
            <p className="text-lg text-slate-600 mb-6">
              Scan to view medical information
            </p>
            
            {/* Large QR Code */}
            <div className="bg-white p-6 rounded-2xl shadow-xl inline-block mb-6 border-4 border-slate-200">
              <img src={qrCodeUrl} alt="Emergency QR Code" className="w-64 h-64 sm:w-80 sm:h-80" />
            </div>
            
            <p className="text-sm text-slate-500 mb-6 max-w-sm mx-auto">
              This QR code links to emergency medical information that can be viewed without logging in
            </p>
            
            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={handleCopyLink}
                variant="outline"
                className="h-12 px-6"
              >
                <Copy className="h-5 w-5 mr-2" />
                Copy Link
              </Button>
              <Button
                onClick={handleShare}
                className="h-12 px-6 bg-blue-600 hover:bg-blue-700"
              >
                <Share2 className="h-5 w-5 mr-2" />
                Share
              </Button>
              <Button
                onClick={() => setShowQR(false)}
                className="h-12 px-6 bg-slate-800 hover:bg-slate-900"
              >
                <X className="h-5 w-5 mr-2" />
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmergencyModePage;
